"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAssetUpload = useAssetUpload;
const react_1 = __importDefault(require("react"));
const uuid = __importStar(require("uuid"));
const axios_1 = __importDefault(require("axios"));
const asset_1 = require("@src/types/asset");
const useConfig_1 = require("@src/hooks/useConfig");
const types_1 = require("@src/types");
const useAppMutation_1 = require("@src/hooks/useAppMutation");
const Api = __importStar(require("@src/api"));
const asset_library_model_1 = require("./asset-library.model");
const use_asset_library_notifications_1 = require("./use-asset-library-notifications");
function useAssetUpload({ activeAssetCount, onUploadAssetSuccess }) {
    const config = (0, useConfig_1.useConfig)();
    const [uploadingAssets, setUploadingAssets] = react_1.default.useState([]);
    const { showAssetCountLimitReachedNotification, showInvalidFileTypeNotification, showAssetFileTooLargeNotification, showAssetsFeatureDisabledNotification, showGeneralErrorNotification, } = (0, use_asset_library_notifications_1.useAssetLibraryNotifications)();
    function enqueueAssetFileForUpload(file) {
        const assetType = (0, asset_library_model_1.getAssetTypeFromFile)(file);
        const maxFileSizeInMb = config.assets.sizeLimitsInMb[assetType];
        if (activeAssetCount + uploadingAssets.length >= config.assets.countLimit) {
            showAssetCountLimitReachedNotification(config.assets.countLimit);
            return;
        }
        if (!(0, asset_library_model_1.isAssetFileTypeValid)(file)) {
            showInvalidFileTypeNotification(file.name);
            return;
        }
        if (!(0, asset_library_model_1.isAssetFileSizeValid)(file, maxFileSizeInMb)) {
            showAssetFileTooLargeNotification(file.name, maxFileSizeInMb);
            return;
        }
        const newUploadingAsset = {
            file,
            isUploading: false,
            uuid: uuid.v4(),
            uploadStartedAt: new Date(),
            type: (0, asset_library_model_1.getAssetTypeFromFile)(file),
            progressPercentage: 0,
        };
        setUploadingAssets(prevUploadingAssets => [...prevUploadingAssets, newUploadingAsset]);
    }
    react_1.default.useEffect(() => {
        const pendingAssets = uploadingAssets.filter(asset => asset.isUploading === false);
        pendingAssets.forEach(asset => {
            markAssetAsUploading(asset);
            uploadAsset(asset);
        });
    }, [uploadingAssets]);
    function markAssetAsUploading(targetAsset) {
        setUploadingAssets(prevUploadingAssets => prevUploadingAssets.map(asset => asset.uuid === targetAsset.uuid ? { ...asset, isUploading: true } : asset));
    }
    function removeUploadingAsset(targetAsset) {
        setUploadingAssets(prevUploadingAssets => prevUploadingAssets.filter(asset => asset.uuid !== targetAsset.uuid));
    }
    function updateAssetProgressPercentage(targetAsset, progressPercentage) {
        setUploadingAssets(prevUploadingAssets => prevUploadingAssets.map(asset => asset.uuid === targetAsset.uuid ? { ...asset, progressPercentage } : asset));
    }
    async function uploadImageOrDocumentAndGetSavedAsset(uploadingImageOrDocumentAsset) {
        return Api.Asset.uploadImageOrDocumentAsset({
            uploadingAsset: uploadingImageOrDocumentAsset,
            onUploadProgress: percentage => updateAssetProgressPercentage(uploadingImageOrDocumentAsset, percentage),
        });
    }
    async function uploadVideoToMux(muxVideoUploadUrl, uploadingVideoAsset) {
        await axios_1.default.put(muxVideoUploadUrl, uploadingVideoAsset.file, {
            transformRequest: (data, headers) => {
                // eslint-disable-next-line no-param-reassign
                delete headers.common[types_1.LANGUAGE_HEADER_NAME];
                return data;
            },
            onUploadProgress: (progressEvent) => {
                const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                updateAssetProgressPercentage(uploadingVideoAsset, percentage);
            },
        });
    }
    async function uploadVideoAndGetSavedAsset(uploadingVideoAsset) {
        const { muxVideoUploadUrl, muxDirectUploadId } = await Api.Asset.getUrlForVideoUpload();
        await uploadVideoToMux(muxVideoUploadUrl, uploadingVideoAsset);
        const savedAsset = await Api.Asset.saveUploadedVideoAsset({
            uploadingAsset: uploadingVideoAsset,
            muxDirectUploadId,
        });
        return savedAsset;
    }
    const { mutateAsync: uploadAsset } = (0, useAppMutation_1.useAppMutation)(async (uploadingAsset) => {
        if (uploadingAsset.type === asset_1.AssetType.VIDEO) {
            return uploadVideoAndGetSavedAsset(uploadingAsset);
        }
        if (uploadingAsset.type === asset_1.AssetType.DOCUMENT || uploadingAsset.type === asset_1.AssetType.IMAGE) {
            return uploadImageOrDocumentAndGetSavedAsset(uploadingAsset);
        }
        throw new Error(`Cannot upload asset: Invalid asset type ${uploadingAsset.type}`);
    }, {
        onSuccess: onUploadAssetSuccess,
        onError: (err) => {
            var _a;
            console.error(err);
            const code = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
            if (code === 402) {
                showAssetsFeatureDisabledNotification();
                return;
            }
            if (code === 409) {
                showAssetCountLimitReachedNotification(config.assets.countLimit);
                return;
            }
            showGeneralErrorNotification();
        },
        onSettled: (_, __, uploadingAsset) => {
            removeUploadingAsset(uploadingAsset);
        },
    });
    return { uploadingAssets, enqueueAssetFileForUpload };
}
